import React, { useEffect, useContext, useState } from "react";
import Header from "../../components/Layout/Header";
import Sidebar from "../../components/Layout/Sidebar";
import Main from "../../components/Layout/Main";
import AuthContext from "../../context/auth/authContext";
import userCan from "../../config/user_can";
import CustomersContext from "../../context/otogas/customers/customersContext";
import { Modal, Button, Spinner } from "react-bootstrap";
import SweetAlertWrapper from "../../components/Layout/sweetAlert";

import PriceApprovalModal from "./approvalRequest";
import AssignApproverModal from "./addApprover";

const ListPriceApprovals = () => {
    const page = "Price Approvals";
    const { user } = useContext(AuthContext);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });

    const {
        price_approvals = [],
        listPriceApprovals,
        approvePrice,
        addPriceApprovalRequest,
        loading,
        notification,
        clear_notification,
    } = useContext(CustomersContext);


    // Modals
    const [showNewRequestModal, setShowNewRequestModal] = useState(false);
    const [showGlobalApproverModal, setShowGlobalApproverModal] = useState(false);
    const [approveTarget, setApproveTarget] = useState(null);

    // loaders
    const [creating, setCreating] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [approvingId, setApprovingId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const canApprove = userCan(user, "POST:GasPriceApproval:approve");

    const refreshList = async () => {
        setRefreshing(true);
        try {
            await listPriceApprovals?.();
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        refreshList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text,
                type: notification.type,
            });
            setShowAlert(true);
        }
    }, [notification]);

    const handleConfirm = () => {
        setShowAlert(false);
        clear_notification();
        listPriceApprovals()
    };


    const confirmApprove = (row) => setApproveTarget(row);

    const handleApproveConfirmed = async () => {
        if (!approveTarget) return;
        const approvalCode = approveTarget.approvalCode;

        setApprovingId(approvalCode);
        try {
            await approvePrice?.(approvalCode);
            setApproveTarget(null);
            await refreshList();
        } finally {
            setApprovingId(null);
        }
    };

    const rowApproveDisabled = (row) => {
        const status = (row.status || "").toLowerCase();
        const used = (row.isUsed || "").toLowerCase();
        const isRowLoading = approvingId === (row.approvalCode || "");
        return status === "approved" || used === "used" || !canApprove || isRowLoading;
    };

    return (
        <>
            <Header />
            <Sidebar />
            <Main page={page}>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={alertMessage.text}
                    onConfirm={handleConfirm}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Price Approvals</h5>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowGlobalApproverModal(true)}
                                    disabled={assigning}
                                >
                                    {assigning ? (
                                        <>
                                            <Spinner as="span" size="sm" animation="border" className="me-2" />
                                            Opening...
                                        </>
                                    ) : (
                                        " Add Approver"
                                    )}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => setShowNewRequestModal(true)}
                                    disabled={creating}
                                >
                                    {creating ? (
                                        <>
                                            <Spinner as="span" size="sm" animation="border" className="me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Add New Price Request"
                                    )}
                                </Button>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Number Plate</th>
                                        <th>Qty</th>
                                        <th>Original</th>
                                        <th>Proposed</th>
                                        <th>Initiator</th>
                                        <th>Approver</th>
                                        <th>Status</th>
                                        <th>Used?</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading || refreshing ? (
                                        <tr>
                                            <td colSpan="10" className="text-center text-muted">
                                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : price_approvals.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="text-center text-muted">
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        price_approvals.map((r, idx) => {
                                            const original = Number(r.originalPrice ?? 0);
                                            const proposed = Number(r.proposedPrice ?? 0);
                                            const status = (r.status || "Pending").toLowerCase();
                                            const used = (r.isUsed || "Not Used").toLowerCase();
                                            const isRowLoading = approvingId === (r.approvalCode || "");

                                            return (
                                                <tr key={r.approvalCode || idx}>
                                                    <td>{r.approvalCode || <span className="text-muted">—</span>}</td>
                                                    <td className="text-uppercase">{r.numberPlate}</td>
                                                    <td>{r.quantity}</td>
                                                    <td>{original.toFixed(2)}</td>
                                                    <td>{proposed.toFixed(2)}</td>
                                                    <td>{r.initiator}</td>
                                                    <td>{r.approverName || <span className="text-muted">—</span>}</td>
                                                    <td>
                                                        <span
                                                            className={
                                                                status === "approved"
                                                                    ? "badge bg-success"
                                                                    : status === "pending"
                                                                        ? "badge bg-warning"
                                                                        : status === "rejected"
                                                                            ? "badge bg-danger"
                                                                            : "badge bg-secondary"
                                                            }
                                                        >
                                                            {r.status || "Pending"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={used === "used" ? "badge bg-secondary" : "badge bg-info"}>
                                                            {r.isUsed || "Not Used"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <button
                                                                className="btn btn-sm btn-custom-actions dropdown-toggle"
                                                                type="button"
                                                                id={`dropdownMenu-${idx}`}
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                                disabled={isRowLoading}
                                                            >
                                                                {isRowLoading ? (
                                                                    <>
                                                                        <Spinner as="span" size="sm" animation="border" className="me-2" />
                                                                        Working
                                                                    </>
                                                                ) : (
                                                                    "Actions"
                                                                )}
                                                            </button>
                                                            <ul className="dropdown-menu" aria-labelledby={`dropdownMenu-${idx}`}>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => setApproveTarget(r)}
                                                                        disabled={rowApproveDisabled(r)}
                                                                    >
                                                                        Approve
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="small text-muted">
                            Showing {price_approvals.length} record{price_approvals.length === 1 ? "" : "s"}
                        </div>
                    </div>
                </div>
            </Main>

            {/* New Request Modal */}
            <PriceApprovalModal
                show={showNewRequestModal}
                submitting={creating}
                handleClose={() => setShowNewRequestModal(false)}
                handleSave={async (payload) => {
                    setCreating(true);
                    try {
                        await addPriceApprovalRequest?.(payload);
                        setShowNewRequestModal(false);
                        await refreshList();
                    } finally {
                        setCreating(false);
                    }
                }}
            />

            {/* GLOBAL Approver Modal */}
            <AssignApproverModal
                show={showGlobalApproverModal}
                handleClose={() => setShowGlobalApproverModal(false)}
                submitting={assigning}
                setSubmitting={setAssigning}
                onSaved={async () => {
                    setShowGlobalApproverModal(false);
                    await refreshList();
                }}
            />

            {/* Approve Confirmation Modal */}
            <Modal show={!!approveTarget} onHide={() => setApproveTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Approval</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to approve{" "}
                    <strong>{approveTarget?.numberPlate}</strong> ({approveTarget?.approvalCode})?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setApproveTarget(null)} disabled={approvingId !== null}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleApproveConfirmed} disabled={approvingId !== null}>
                        {approvingId !== null ? (
                            <>
                                <Spinner as="span" size="sm" animation="border" className="me-2" />
                                Approving...
                            </>
                        ) : (
                            "Yes, Approve"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ListPriceApprovals;
